import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogConfig,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { HypervisorOption, HypervisorType } from '../../../../../../core/enums';
import { DialogComponent, DialogSize } from '../../../../../../core/models/dialog.data';
import {
  DEFAULT_PROVISIONING_ESTIMATE_SECONDS,
  getProvisioningStepTranslationKey,
  ProvisioningStep,
} from '../../../../../../core/models';
import { VmProvisionService } from '../../../../../../core/services';
import { AuthStore } from '../../../../../../core/stores';

export interface AddVmDialogData {
  customerId?: number;
}

@Component({
  selector: 'dbx-add-vm-dialog',
  templateUrl: './add-vm-dialog.component.html',
  styleUrl: './add-vm-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class AddVmDialogComponent
  extends DialogComponent<AddVmDialogComponent, AddVmDialogData, boolean>
  implements OnInit, OnDestroy
{
  readonly provisionService = inject(VmProvisionService);
  private readonly dialogData = inject<AddVmDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly authStore = inject(AuthStore, { optional: true });

  readonly hypervisorOptions: readonly HypervisorOption[] = HypervisorType.options;
  readonly selectedHypervisor = signal<HypervisorType | null>(HypervisorType.VirtualBox);
  readonly vmName = signal<string>('');
  readonly copiedChecksum = signal<boolean>(false);
  readonly copiedCmdWindows = signal<boolean>(false);
  readonly copiedCmdLinux = signal<boolean>(false);
  readonly guideOpen = signal<boolean>(false);

  readonly state = this.provisionService.$state;

  readonly provisioningStepTranslationKey = computed(() => {
    const currentStep = this.state().currentStep || 'PREPARING';
    const hv =
      this.state().hypervisor ||
      (this.selectedHypervisor() ? HypervisorType.toHypervisor(this.selectedHypervisor()!) : undefined);
    return getProvisioningStepTranslationKey(currentStep, hv);
  });

  readonly provisioningCountdownSeconds = computed(() => {
    const est = this.state().estimatedRemainingSeconds;
    if (est !== null && est !== undefined) {
      return est;
    }
    return Math.max(DEFAULT_PROVISIONING_ESTIMATE_SECONDS - (this.state().elapsedSeconds || 0), 0);
  });

  readonly provisioningProgressPercent = computed(() => {
    const currentStep = this.state().currentStep || 'PREPARING';
    const range = this.getStepProgressRange(currentStep);
    return this.calculateCombinedProgress(range);
  });

  private getStepProgressRange(step: ProvisioningStep): { min: number; max: number } {
    const stepRanges: Record<ProvisioningStep, { min: number; max: number }> = {
      PREPARING: { min: 0, max: 15 },
      CUSTOMIZING: { min: 15, max: 30 },
      CONVERTING: { min: 30, max: 75 },
      PACKAGING: { min: 75, max: 99 },
      COMPLETED: { min: 100, max: 100 },
      FAILED: { min: 0, max: 0 },
    };
    return stepRanges[step] ?? { min: 0, max: 99 };
  }

  private calculateCombinedProgress(range: { min: number; max: number }): number {
    const backendProgress = this.state().progress || 0;
    const baseProgress = Math.max(range.min, backendProgress);
    const elapsed = this.state().elapsedSeconds || 0;
    const timeProgress = Math.round((elapsed / DEFAULT_PROVISIONING_ESTIMATE_SECONDS) * 100);

    const combined = Math.max(baseProgress, Math.min(timeProgress, range.max));
    return Math.min(Math.max(combined, range.min), range.max);
  }

  private static readonly DIALOG_WIDTH = 'min(820px, 90vw)';
  private static readonly DIALOG_HEIGHT = 'min(640px, 90vh)';

  override dialogSize(): DialogSize {
    return DialogSize.AUTO;
  }

  override dialogConfig(): MatDialogConfig<AddVmDialogData> {
    return {
      ...super.dialogConfig(),
      width: AddVmDialogComponent.DIALOG_WIDTH,
      height: AddVmDialogComponent.DIALOG_HEIGHT,
    };
  }

  constructor() {
    super();
    if (this.authStore) {
      effect(() => {
        if (!this.authStore?.authenticated()) {
          this.close();
        }
      });
    }

    effect(() => {
      const open = this.guideOpen();
      if (this.dialogRef?.updateSize) {
        this.dialogRef.updateSize(
          AddVmDialogComponent.DIALOG_WIDTH,
          open ? 'auto' : AddVmDialogComponent.DIALOG_HEIGHT,
        );
      }
    });
  }

  ngOnInit(): void {
    if (this.state().status === 'completed' || this.state().status === 'error') {
      this.provisionService.cancel();
    }
  }

  ngOnDestroy(): void {
    this.provisionService.cancel();
  }

  isPhaseCompleted(step: ProvisioningStep): boolean {
    const order: ProvisioningStep[] = ['PREPARING', 'CUSTOMIZING', 'CONVERTING', 'PACKAGING', 'COMPLETED'];
    const currentStep = this.state().currentStep || 'PREPARING';
    const currentIndex = order.indexOf(currentStep);
    const targetIndex = order.indexOf(step);
    return targetIndex < currentIndex || this.state().status === 'completed';
  }

  selectHypervisor(hv: HypervisorType): void {
    if (this.state().status === 'idle') {
      this.selectedHypervisor.set(hv);
    }
  }

  startDownload(): void {
    const hv = this.selectedHypervisor();
    if (!hv) {
      return;
    }
    this.provisionService.startProvisioning({
      hypervisor: hv,
      name: this.vmName().trim() || undefined,
      customerId: this.dialogData?.customerId,
    });
  }

  cancelDownload(): void {
    this.provisionService.cancel();
  }

  toggleGuide(): void {
    this.guideOpen.update((v) => !v);
  }

  async copyChecksum(): Promise<void> {
    const checksum = this.state().checksum;
    if (!checksum) return;
    const ok = await this.provisionService.copyToClipboard(checksum);
    if (ok) {
      this.copiedChecksum.set(true);
      setTimeout(() => this.copiedChecksum.set(false), 2000);
    }
  }

  async copyCommand(cmd: string, type: 'windows' | 'linux'): Promise<void> {
    const ok = await this.provisionService.copyToClipboard(cmd);
    if (ok) {
      if (type === 'windows') {
        this.copiedCmdWindows.set(true);
        setTimeout(() => this.copiedCmdWindows.set(false), 2000);
      } else {
        this.copiedCmdLinux.set(true);
        setTimeout(() => this.copiedCmdLinux.set(false), 2000);
      }
    }
  }

  getWindowsVerifyCommand(): string {
    const fn = this.state().fileName || 'image.ova';
    return `Get-FileHash .\\${fn} -Algorithm SHA256`;
  }

  getLinuxVerifyCommand(): string {
    const fn = this.state().fileName || 'image.ova';
    return `sha256sum ${fn}`;
  }

  formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  formatSpeed(speedBytesPerSec: number): string {
    if (!speedBytesPerSec || speedBytesPerSec <= 0) return '0 MB/s';
    return `${this.formatBytes(speedBytesPerSec)}/s`;
  }

  formatEta(seconds: number | null): string {
    if (seconds === null || seconds === undefined) return '--';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }

  resetForm(): void {
    this.provisionService.cancel();
    this.vmName.set('');
    this.guideOpen.set(false);
  }

  handleClose(): void {
    const isCompleted = this.state().status === 'completed';
    this.provisionService.cancel();
    this.close(isCompleted);
  }
}
